import { EPSelect, EPSelectOption } from '@/components';
import React from 'react';
import { bandConfigurationOptions, LONG_DASH } from '../../EngineVersionUtils';
import { Tooltip } from 'antd';
import TableHeaderCell from '../../Engine/Common/TableHeaderCell/TableHeaderCell';
import EPCheckbox from '@/comp/EPCheckbox';
import cn from 'classnames';
import { t } from 'i18next';
import { Setpoint } from '@/store/Engine/common';
import { Range } from '@/store/Engine/MainRangesConfiguration';

export enum RangeType {
  Flow = 'flow',
  Pressure = 'pressure',
}

export interface RangeProps {
  unit: string;
  type: RangeType;
  range?: Partial<Range>;
  setpoints?: Setpoint[];
  fromFallbackOverview?: boolean;
  flowIsFirst?: boolean;
  hasTriedToSubmit?: boolean;
}

export const getOnSetpointFieldChange = engineStore => (key, field) => e => {
  const changedValue = field === 'modulate' ? e?.target?.checked : field === 'bandConfiguration' ? e : e?.target?.value;
  engineStore.setpointsConfiguration.setSetpointField(key, field, changedValue);
};

export const formatRenderedValue = renderedValue => {
  const convertedValue = Math.round(renderedValue * 1000) / 1000;

  if (renderedValue === null || isNaN(convertedValue)) return <span>&mdash;</span>;

  return convertedValue;
};

export const getRulesColumns = (
  rules,
  getEditableFieldsStatusForCompressor,
  compressorTooltipContents,
  formatRenderedValue,
  onSetpointFieldChange,
  onInputClick,
  engineStore,
  fromFallbackOverview,
  setIsDropdownOpen,
) => [
  {
    dataIndex: 'compressorNumber',
    key: 'compressorNumber',
    title: <TableHeaderCell titleKey="no" />,
    width: '14%',

    render: compressorNumber => <div className="table-cell">{compressorNumber ?? <span>&mdash;</span>}</div>,
  },
  ...rules,
  {
    dataIndex: 'modulate',
    key: 'modulate',
    title: <TableHeaderCell titleKey="mod" />,
    width: '16%',
    render: (modulate, { key, compressorId }) => (
      <div
        className={cn(
          'table-cell',
          'modulate-checkbox-container',
          modulate !== engineStore.setpointsConfiguration?.getOldSetpointByKey(key)?.modulate,
        )}
      >
        {!getEditableFieldsStatusForCompressor('modulate', modulate, compressorId, key)?.editableFlag ? (
          <div>&mdash;</div>
        ) : (
          <EPCheckbox
            data-testid="ep-checkbox"
            checked={modulate}
            className={
              fromFallbackOverview || engineStore.isReadOnly
                ? 'view-mode-modulate-checkbox'
                : 'edit-mode-modulate-checkbox'
            }
            onClick={onInputClick}
            onChange={onSetpointFieldChange(key, 'modulate')}
            disabled={fromFallbackOverview || engineStore.isReadOnly}
          />
        )}
      </div>
    ),
  },
  {
    dataIndex: 'bandConfiguration',
    key: 'bandConfiguration',
    title: <TableHeaderCell titleKey="bnd" />,
    width: '16%',
    render: (bandConfiguration, { key, compressorId }: { key; compressorId }) => {
      const { editableFlag } = getEditableFieldsStatusForCompressor(
        'bandConfiguration',
        bandConfiguration,
        compressorId,
        key,
      );

      const tooltipContent = compressorTooltipContents[compressorId] || <>{t('noMetricsAvailable')}</>;

      const renderSelectOptions = () =>
        bandConfigurationOptions.map(option => (
          <EPSelectOption value={option} key={option}>
            {option !== LONG_DASH ? (
              <Tooltip title={tooltipContent} placement="right">
                <span>{option}</span>
              </Tooltip>
            ) : (
              <span>{option}</span>
            )}
          </EPSelectOption>
        ));

      // Not editable case
      if (!editableFlag) {
        return (
          <div className="table-cell">
            <span>{LONG_DASH}</span>
          </div>
        );
      }

      // Editable mode
      if (!fromFallbackOverview && !engineStore.isReadOnly) {
        return (
          <div className="table-cell">
            <div style={{ width: '100%' }}>
              <EPSelect
                value={bandConfiguration ?? LONG_DASH}
                onChange={onSetpointFieldChange(key, 'bandConfiguration')}
                onClick={onInputClick}
                size="small"
                onDropdownVisibleChange={setIsDropdownOpen}
              >
                {renderSelectOptions()}
              </EPSelect>
            </div>
          </div>
        );
      }

      // View mode with editable flag
      return (
        <div className="table-cell">
          {bandConfiguration && bandConfiguration !== LONG_DASH ? (
            <Tooltip title={tooltipContent} placement="right">
              <span style={{ cursor: 'pointer' }}>{formatRenderedValue(bandConfiguration)}</span>
            </Tooltip>
          ) : (
            <span>{formatRenderedValue(bandConfiguration)}</span>
          )}
        </div>
      );
    },
  },
];
